/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 선택자 클래스를 정의한다.
 *
 * @file /src/models/table/Selection.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 16.
 */
import { Table } from '@models/table/Table';
import { Record } from '@models/store/Record';
import { Row } from '@models/table/Row';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Chunks } from '@utils/Chunks';
import { Language } from '@utils/Language';
import { Utils } from '@namespaces/Utils';
import './Selection.css';

export interface Properties {
    /**
     * 테이블
     */
    table: Table;

    /**
     * 선택가능여부
     */
    selectable?: boolean | ((record: Record) => boolean);

    /**
     * 다중선택 여부
     */
    multiple?: boolean;

    /**
     * 어떤방식으로도 항상 이전선택을 유지하며 다중선택을 할지 여부
     */
    additive?: boolean;

    /**
     * 선택된 항목을 선택해제 할 수 있는지 여부 (multiple 이 true 인 경우 항상 true)
     */
    deselectable?: boolean;

    /**
     * 선택사항 보관여부 (페이지 이동 등으로 그리드 데이터가 변경되더라도 이전 선택사항을 보관할 지 여부)
     */
    keepable?: boolean;

    /**
     * 트리에서 부모 항목 선택 시 하위 항목을 함께 선택할지 여부 (기본값 true)
     */
    cascade?: boolean;

    /**
     * 선택된 항목 개수 노출 여부, 선택 해제 버튼 여부, 선택 완료 버튼 여부
     */
    selectView?: {
        alwaysShow?: boolean;
        count?: boolean;
        deselect?: boolean;
        confirm?: boolean;
    };
}

export class Selection {
    /**
     * 테이블
     */
    table: Table;

    /**
     * 선택된 레코드
     */
    selections: Map<string, Record> = new Map();

    /**
     * 선택가능여부
     */
    selectable: boolean | ((record: Record) => boolean);

    /**
     * 다중선택 여부
     */
    multiple: boolean;

    /**
     * 어떤방식으로도 항상 이전선택을 유지하며 다중선택을 할지 여부
     */
    additive: boolean;

    /**
     * 선택된 항목을 선택해제 할 수 있는지 여부 (multiple 이 true 인 경우 항상 true)
     */
    deselectable: boolean;

    /**
     * 선택사항 보관여부 (페이지 이동 등으로 그리드 데이터가 변경되더라도 이전 선택사항을 보관할 지 여부)
     */
    keepable: boolean;

    /**
     * 트리에서 부모 항목 선택 시 하위 항목을 함께 선택할지 여부
     */
    cascade: boolean;

    /**
     * 선택된 항목 개수 노출 여부, 선택 해제 버튼 여부, 선택 완료 버튼 여부
     */
    selectView: {
        alwaysShow?: boolean;
        count?: boolean;
        deselect?: boolean;
        confirm?: boolean;
    } | null = null;

    /**
     * 마지막으로 선택된 행의 레코드
     */
    latestSelection: Record = null;

    /**
     * 헤더 체크박스 DOM
     */
    $header: Dom = null;

    /**
     * 행별 체크박스 DOM
     */
    $checks: WeakMap<Row, Dom> = new WeakMap();

    /**
     * 선택된 항목의 개수를 보여주는 Dom
     */
    $selectionView: Dom = null;

    /**
     * 선택해제 버튼 Dom
     */
    $deselectButton: Dom = null;

    /**
     * 선택완료 버튼 Dom
     */
    $confirmButton: Dom = null;

    /**
     * 테이블 선택자 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties = null) {
        this.table = properties.table;
        this.selectable = properties?.selectable ?? false;
        this.multiple = properties?.multiple == true;
        this.additive = properties?.additive === true && this.multiple == true;
        this.deselectable = this.multiple == true || properties?.deselectable == true;
        this.keepable = properties?.keepable ?? false;
        this.cascade = properties?.cascade !== false;
        this.selectView = properties?.selectView ?? null;

        /**
         * 데이터스토어의 이벤트를 받아, 선택사항을 갱신한다.
         */
        this.getTable().addEvent('beforeLoad', async () => {
            if (this.keepable == false) {
                this.selections.clear();
            }
        });

        this.getTable().addEvent('load', async () => {
            this.#restoreSelections();
        });

        this.getTable().addEvent('update', async () => {
            this.#updateSelections();
        });
    }

    /**
     * 테이블을 가져온다.
     *
     * @returns 테이블
     */
    getTable(): Table {
        return this.table;
    }

    /**
     * 다중선택인 경우 테이블 헤더에 표시될 DOM 을 가져온다.
     *
     * @param is_button - 버튼의 DOM 을 가져올지 여부
     * @returns 헤더 DOM
     */
    $getHeader(is_button: boolean = false): Dom {
        if (this.$header == null) {
            this.$header = Html.create('div', { 'data-model': 'table', 'data-role': 'column', 'data-index': '-1' });

            const $button = Html.create('button', { type: 'button' }, '<i></i>');
            $button.on('click', ($button, e) => {
                if ($button.hasClass('checked') == true) {
                    this.deselectAll();
                } else {
                    this.selectAll();
                }

                e.stopImmediatePropagation();
            });

            this.$header.append($button);
        }

        if (is_button == true) {
            return Html.get('button', this.$header);
        }

        return this.$header;
    }

    /**
     * 다중선택인 경우 각 행의 체크박스 DOM 을 가져온다.
     *
     * @returns 각 행의 체크박스 DOM
     */
    $getBody(row: Row): Dom {
        if (this.$checks.has(row) === false) {
            const $check = Html.create('div', { 'data-model': 'table', 'data-role': 'column', 'data-index': '-1' });
            const $button = Html.create('button', { type: 'button' }, '<i></i>');
            $check.append($button);

            if (this.isSelectable(row.getRecord()) == false) {
                $button.disable();
            }

            $check.on('click', async (_, e) => {
                e.stopImmediatePropagation();

                if (this.isSelectable(row.getRecord()) == false) {
                    return;
                }

                if (row.$getRow().getAttr('disabled') === 'disabled') {
                    return;
                }

                if (this.isSelected(row.getRecord().getId()) == true) {
                    this.#deselect(row.getRecord(), true);
                } else {
                    if (e.shiftKey == true && this.latestSelection !== null) {
                        this.#selectRange(this.latestSelection, row.getRecord(), true);
                    } else {
                        this.#select(row.getRecord(), true, true);
                    }
                }
            });

            this.$checks.set(row, $check);
        }

        return this.$checks.get(row);
    }

    /**
     * 선택된 항목의 개수를 보여주는 Dom을 가져온다.
     *
     * @returns 선택된 항목의 개수를 보여주는 Dom
     */
    $getSelectionView(): Dom {
        if (this.$selectionView == null) {
            this.$selectionView = Html.create('div', { 'data-model': 'table', 'data-role': 'selection' });

            if (this.selectView?.count === true) {
                const $text = Html.create('span');
                this.$selectionView.append($text);
            }

            if (this.selectView?.deselect == true || this.selectView?.confirm == true) {
                const $buttons = Html.create('div', { 'class': 'buttons' });

                if (this.selectView?.deselect == true) {
                    $buttons.append(this.$getDeselectionButton());
                }

                if (this.selectView?.confirm == true) {
                    $buttons.append(this.$getConfirmButton());
                }

                this.$selectionView.append($buttons);
            }
        }
        return this.$selectionView;
    }

    /**
     * 선택해제 버튼 Dom을 가져온다.
     *
     * @returns 선택해제 버튼 Dom
     */
    $getDeselectionButton(): Dom {
        if (this.$deselectButton == null) {
            this.$deselectButton = Html.create('button', { type: 'button' });
            this.$deselectButton.html(Language.get('models.table.selection.deselect'));

            this.$deselectButton.on('click', () => {
                Html.get(
                    'div[data-model=table][data-role=column].focus',
                    this.getTable().getBody().$getBody()
                )?.removeClass('focus');
                // this.#deselectAll('row');
            });
        }

        return this.$deselectButton;
    }

    /**
     * 선택완료 버튼 Dom을 가져온다.
     *
     * @returns 선택완료 버튼 Dom
     */
    $getConfirmButton(): Dom {
        if (this.$confirmButton == null) {
            this.$confirmButton = Html.create('button', { type: 'button' });
            this.$confirmButton.html(Language.get('models.table.selection.confirm'));

            this.$confirmButton.on('click', () => {
                Html.get(
                    'div[data-model=table][data-role=column].focus',
                    this.getTable().getBody().$getBody()
                )?.removeClass('focus');
                this.getTable().fireEvent('selectionChange', [[...this.selections.values()], 'row', this.table]);
            });
        }

        return this.$confirmButton;
    }

    /**
     * 선택된 항목의 개수를 변경한다.
     */
    setSelectionView(): void {
        const $selectionView = this.$getSelectionView();
        const $text = Html.get('span', $selectionView);

        if (this.selections.keys().toArray().length > 0) {
            if (this.isAlwaysShow() != true) {
                this.$getSelectionView().show();
            }
        } else {
            if (this.isAlwaysShow() != true) {
                this.$getSelectionView().hide();
            }
        }

        if (this.selectView?.count === true) {
            $text.html(
                Language.get('texts.selections_count', { count: this.selections.keys().toArray().length.toString() })
            );
        }
    }

    /**
     * 다중선택가능여부를 가져온다.
     *
     * @returns 다중선택여부
     */
    isMultiple(): boolean {
        return this.multiple;
    }

    /**
     * 선택된 항목을 보여주는 뷰를 노출할지 여부를 가져온다.
     *
     * @returns 선택된 항목을 보여주는 뷰를 노출할지 여부
     */
    isSelectView(): boolean {
        return this.multiple == true && this.selectView != null;
    }

    /**
     * 선택된 항목을 보여주는 뷰를 계속 노출할지 여부를 가져온다.
     *
     * @returns 선택된 항목을 보여주는 뷰를 계속 노출할지 여부
     */
    isAlwaysShow(): boolean {
        return this.isSelectView() == true && this.selectView.alwaysShow == true;
    }

    /**
     * 선택된 레코드를 반환한다.
     *
     * @returns 선택된 레코드 목록 및 테이블
     */
    getSelections(): Record[] {
        return Array.from(this.selections.values());
    }

    /**
     * 선택상태를 특정 선택사항으로 롤백한다.
     *
     * @param selections - 롤백할 선택사항
     * @param is_focus - 마지막 선택행에 포커스를 지정할지 여부
     * @param focused - 컴포넌트에 포커스를 실제로 지정할지 여부
     */
    async restoreSelections(
        selections: Record[] | null,
        is_focus: boolean = false,
        focused: boolean = false
    ): Promise<void> {
        await this.#deselectAll(true, false);

        selections ??= [];
        let focusRow: string = null;
        for (const record of selections) {
            let row = this.getTable().getBody().getRowById(record.getId());

            /**
             * 만약 Tree 인 경우 부모 ROW 를 찾아 모두 확장한다.
             */
            if (row === null || this.getTable().isTree() == true) {
                let parents: Record[] = [];
                let parent = record.getParent();
                while (parent !== null) {
                    parents.push(parent);
                    parent = parent.getParent();
                }

                while (parents.length > 0) {
                    const parent = parents.pop();
                    const row = this.getTable().getBody().getRowById(parent.getId());
                    await row?.expand();
                }

                row = this.getTable().getBody().getRowById(record.getId());
            }

            row?.$getRow().addClass('select');
            this.selections.set(record.getId(), record);

            const $record = Html.get(
                `div[data-role=row][data-record-id="${record.getId()}"]`,
                this.getTable().getBody().$getBody()
            );
            if ($record !== null) {
                focusRow = record.getId();
            }
        }

        if (is_focus == true && focusRow !== null) {
            const row = this.getTable().getBody().getRowById(focusRow);
            await this.getTable().getBody().focusRow(row, true, focused);
        }
    }

    /**
     * 현재 선택된 레코드들을 selectable 콜백으로 다시 검증하여,
     * 더 이상 선택할 수 없게 된 레코드는 선택해제한다.
     * (행의 체크박스 표시상태는 isSelectable() 이 렌더링 시점에 직접 계산하므로 별도로 캐시해둘 필요가 없다.)
     */
    revalidateSelections(): void {
        if (typeof this.selectable !== 'function') {
            return;
        }

        for (const record of [...this.selections.values()]) {
            if (this.selectable(record) == false) {
                this.#deselect(record, false);
            }
        }
    }

    /**
     * 특정행을 선택한다.
     *
     * @param record - 선택할 레코드정보
     * @param options - 기존 선택행을 유지한 채 다중선택을 할지 여부 또는 마우스 포인트 이벤트
     */
    async select(record_id: string | Record, options: boolean | Event = false): Promise<void> {
        const record = this.#getRecord(record_id);
        if (record === null) {
            return;
        }

        if (this.isSelectable(record) == false) {
            return;
        }

        let is_multiple: boolean = false;
        if (options instanceof Event) {
            if (this.multiple == true) {
                if (options instanceof PointerEvent) {
                    if (this.additive == true || options.ctrlKey == true || options.metaKey == true) {
                        is_multiple = true;
                    }

                    if (options.shiftKey == true && this.latestSelection !== null) {
                        return await this.#selectRange(this.latestSelection, record, true);
                    }
                } else if (this.additive == true) {
                    is_multiple = true;
                }
            }
        } else if (typeof options == 'boolean') {
            is_multiple = options == true || this.additive == true;
        }

        await this.#select(record, is_multiple, true);

        if (options === false) {
            const row = this.getTable().getBody().getRowById(record.getId());
            if (row !== null) {
                await this.getTable().getBody().focusRow(row);
            }
        }
    }

    /**
     * 특점범위를 선택한다.
     *
     * @param startRecordId - 시작행 레코드 고유값
     * @param endRecordId - 종료행 레코드 고유값
     */
    async selectRange(startRecordId: string, endRecordId: string) {
        if (this.isSelectable() == false) {
            return;
        }

        await this.#selectRange(this.#getRecord(startRecordId), this.#getRecord(endRecordId), true);
    }

    /**
     * 전체행을 선택한다.
     */
    async selectAll(): Promise<void> {
        if (this.isSelectable() == false) {
            return;
        }

        await this.#selectAll(true);
    }

    /**
     * 트리 행의 펼침/접힘으로 보이는 행이 바뀔 때 헤더 체크박스 상태를 갱신한다.
     * 선택사항은 변경되지 않으므로 selectionChange 이벤트는 발생시키지 않는다.
     */
    async notifyVisibilityChange(): Promise<void> {
        if (this.getTable().isTree() == true && this.isCascade() == false && this.multiple == true) {
            await this.#selectionChange(false);
        }
    }

    /**
     * 특정행을 선택해제한다.
     *
     * @param record_id - 선택해제할 레코드정보
     */
    async deselect(record_id: string | Record): Promise<void> {
        // @todo this.deselectable == false  확인
        await this.#deselect(this.#getRecord(record_id), true);
    }

    /**
     * 전체행을 선택해제한다.
     */
    async deselectAll(): Promise<void> {
        // @todo keepable 인 경우 다른 페이지의 선택사항도 해제할지 확인창
        await this.#deselectAll(false, true);
    }

    /**
     * 선택사항을 초기화한다.
     */
    async reset(): Promise<void> {
        await this.#deselectAll(true, true);
    }

    /**
     * 특정 레코드가 선택중인지 확인한다.
     *
     * @param record_id - 레코드고유값
     * @returns 선택여부
     */
    isSelected(record_id: string | Record): boolean {
        const record = this.#getRecord(record_id);
        if (record === null) {
            return false;
        }

        return this.selections.has(record.getId());
    }

    /**
     * 종속모드인지 확인한다.
     *
     * @returns 종속모드여부
     */
    isCascade(): boolean {
        return this.getTable().isTree() == true && this.multiple == true && this.cascade == true;
    }

    /**
     * 레코드 선택이 가능한지 확인한다.
     * cascade 모드인 경우 조상이 선택되어 있으면 선택할 수 없다. (자기 자신의 선택여부는 영향을 주지 않는다.)
     *
     * @param record - 선택가능한지 확인할 레코드
     * @returns 선택가능여부
     */
    isSelectable(record: Record | null = null): boolean {
        if (record === null || typeof this.selectable == 'boolean') {
            return this.selectable !== false;
        }

        if (this.selectable(record) == false) {
            return false;
        }

        if (this.isCascade() == true) {
            let parent = record.getParent();
            while (parent !== null) {
                if (this.isSelected(parent.getId()) == true) {
                    return false;
                }
                parent = parent.getParent();
            }
        }

        return true;
    }

    /**
     * 레코드 선택해제가 가능한지 확인한다.
     *
     * @returns 선택해제 가능여부
     */
    isDeselectable(): boolean {
        return this.deselectable;
    }

    /**
     * 레코드고유값, 레코드인덱스 등으로 레코드를 가져온다.
     *
     * @param record_id - 레코드고유값, 레코드인덱스, 레코드
     * @returns 레코드
     */
    #getRecord(record_id: string | Record): Record | null {
        record_id ??= null;
        if (record_id === null) {
            return null;
        }

        if (record_id instanceof Record) {
            return record_id;
        } else {
            return this.getTable().getStore().get(record_id);
        }
    }

    /**
     * 특정행을 선택한다.
     *
     * @param record - 선택할 레코드
     * @param is_multiple - 기존 선택행을 유지한 채 다중선택을 할지 여부
     * @param is_fire_event - 이벤트발생여부
     */
    async #select(record: Record, is_multiple: boolean, is_fire_event: boolean = true): Promise<void> {
        is_multiple = this.multiple == true && is_multiple == true;

        if (record === null) {
            return;
        }

        if (this.isSelectable(record) == false) {
            return;
        }

        if (this.isSelected(record.getId()) == true) {
            if (is_multiple == true || this.getSelections().length == 1) {
                return;
            }
        }

        if (is_multiple == false) {
            await this.#deselectAll(true, false);
        }

        let row = this.getTable().getBody().getRowById(record.getId());

        /**
         * 만약 Tree 인 경우 부모 ROW 를 찾아 모두 확장한다.
         */
        if (row === null && this.getTable().isTree() == true) {
            let parents: Record[] = [];
            let parent = record.getParent();
            while (parent !== null) {
                parents.push(parent);
                parent = parent.getParent();
            }

            while (parents.length > 0) {
                const parent = parents.pop();
                const row = this.getTable().getBody().getRowById(parent.getId());
                await row?.expand();
            }

            row = this.getTable().getBody().getRowById(record.getId());
        }

        row?.$getRow().addClass('select');
        this.selections.set(record.getId(), record);
        this.latestSelection = record;

        if (this.isCascade() == true && row !== null) {
            this.#cascadeSetSelectable(row, false);
        }

        if (is_fire_event == true) {
            await this.#selectionChange();
        }
    }

    /**
     * 트리 cascade 모드에서 특정 행의 모든 로드된 자손의 선택상태 및 체크박스 DOM 을 동기화한다.
     *
     * @param row - 기준 행
     * @param selectable - 선택가능여부
     */
    #cascadeSetSelectable(row: Row, selectable: boolean): void {
        Object.values(row.children).forEach((childRow) => {
            if (selectable === false && this.isSelected(childRow.record.getId()) == true) {
                this.#deselect(childRow.record, false);
            }

            if (this.multiple == true && this.$checks.has(childRow) == true) {
                const $button = Html.get('button', this.$checks.get(childRow));
                selectable === true ? $button?.enable() : $button?.disable();
            }

            this.#cascadeSetSelectable(childRow, selectable);
        });
    }

    /**
     * 특정 행의 체크박스 DOM 을 현재 isSelectable() 값에 맞게 동기화한다.
     * 이미 생성된 체크박스가 없는 경우 아무 작업도 하지 않는다 - 새로 생성될 때 isSelectable() 이
     * 알아서 올바른 상태로 만들어준다.
     *
     * @param row - 동기화할 행
     */
    syncSelectableRow(row: Row): void {
        if (this.$checks.has(row) == false) {
            return;
        }

        const $button = Html.get('button', this.$checks.get(row));
        if (this.isSelectable(row.getRecord()) == true) {
            $button?.enable();
        } else {
            $button?.disable();
        }
    }

    /**
     * 특정범위의 행을 선택한다.
     *
     * @param start - 시작 레코드
     * @param end - 종료 레코드
     * @param is_fire_event - 이벤트발생여부
     */
    async #selectRange(start: Record, end: Record, is_fire_event: boolean = true): Promise<void> {
        if (this.multiple == false) {
            return;
        }

        if (this.isCascade() == true) {
            /**
             * 트리 종속모드: start 와 end 의 부모가 동일한 경우에만 형제 범위를 선택한다.
             */
            const startRow = this.getTable().getBody().getRowById(start.getId());
            const endRow = this.getTable().getBody().getRowById(end.getId());

            if (startRow === null || endRow === null || startRow.parent !== endRow.parent) {
                await this.#select(end, false, true);
                return;
            }

            let siblingIds: string[];
            if (startRow.parent === null) {
                siblingIds = [];
                for (const group of this.getTable().getBody().groups.values()) {
                    siblingIds.push(...group.recordIds);
                }
            } else {
                siblingIds = Object.keys(startRow.parent.children);
            }

            const startIdx = siblingIds.indexOf(start.getId());
            const endIdx = siblingIds.indexOf(end.getId());

            if (startIdx === -1 || endIdx === -1) {
                await this.#select(end, false, true);
                return;
            }

            const [from, to] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
            for (let i = from; i <= to; i++) {
                const record = this.getTable().getStore().get(siblingIds[i]);
                if (record !== null) {
                    await this.#select(record, true, false);
                }
            }
        } else if (this.getTable().isTree() == false || this.isCascade() == false) {
            /**
             * 그리드이거나 독립선택모드: 보이는 모든 행을 선택한다.
             */
            const $rows = this.getTable().getBody().$getVisibleRows();
            const recordIds: string[] = [];

            let startIndex: number = null;
            let endIndex: number = null;
            $rows.forEach(($row, index) => {
                if ($row.getAttr('data-record-id') == start.getId()) {
                    startIndex = index;
                }

                if ($row.getAttr('data-record-id') == end.getId()) {
                    endIndex = index;
                }
                recordIds.push($row.getAttr('data-record-id'));
            });

            if (startIndex === null || endIndex === null) {
                return;
            }

            const indexes = startIndex > endIndex ? [endIndex, startIndex] : [startIndex, endIndex];
            for (let i = indexes[0]; i <= indexes[1]; i++) {
                const record = this.getTable().getStore().get(recordIds[i]);
                if (record !== null) {
                    this.#select(record, true, false);
                }
            }
        }

        this.latestSelection = end;

        if (is_fire_event == true) {
            await this.#selectionChange();
        }
    }

    /**
     * 특정행을 선택해제한다.
     *
     * @param record - 선택해제할 레코드
     * @param is_fire_event - 이벤트발생여부
     */
    async #deselect(record: Record, is_fire_event: boolean = true): Promise<void> {
        if (record == null || this.isSelected(record.getId()) == false) {
            return;
        }

        this.selections.delete(record.getId());

        const row = this.getTable().getBody().getRowById(record.getId());
        row?.$getRow().removeClass('select');
        this.latestSelection = null;

        if (this.isCascade() == true && row !== null) {
            this.#cascadeSetSelectable(row, true);
        }

        if (is_fire_event == true) {
            await this.#selectionChange();
        }
    }

    /**
     * 전체행을 선택한다.
     *
     * @param is_fire_event - 이벤트발생여부
     */
    async #selectAll(is_fire_event: boolean = true): Promise<void> {
        this.$getHeader(true).disable(true);

        await Utils.sleep(100);

        if (this.isCascade() == true) {
            /**
             * 트리 종속모드: 최상위 ROW 만 선택한다.
             */
            for (const group of this.getTable().getBody().groups.values()) {
                const chunk = new Chunks(group.recordIds);
                await chunk.execute(async (recordId) => {
                    const record = this.getTable().getStore().get(recordId);
                    if (record !== null) {
                        await this.#select(record, true, false);
                    }
                });
            }
        } else if (this.getTable().isTree() == true) {
            /**
             * 트리 독립선택모드: 현재 펼쳐진(보이는) 행만 선택한다.
             */
            const recordIds = this.getTable().getBody().$getVisibleRows().getAttrs('data-record-id');
            const chunk = new Chunks(recordIds);
            await chunk.execute(async (recordId) => {
                const record = this.getTable().getStore().get(recordId);
                if (record !== null) {
                    await this.#select(record, true, false);
                }
            });
        } else {
            /**
             * 그리드: 스토어의 모든 행을 선택한다.
             */
            const chunk = new Chunks(this.getTable().getStore().getRecords());
            await chunk.execute(async (record) => {
                await this.#select(record, true, false);
            });
        }

        this.latestSelection = null;
        if (is_fire_event == true) {
            await this.#selectionChange();
        }

        this.$getHeader(true).enable();
    }

    /**
     * 전체행을 선택해제한다.
     *
     * @param is_all - 보관중인 선택사항 전체를 선택해제할지 여부 (false 인 경우 현재 보이고 있는 선택행만 선택해제한다.)
     * @param is_fire_event - 이벤트발생여부
     */
    async #deselectAll(is_all: boolean, is_fire_event: boolean): Promise<void> {
        if (is_fire_event == true) {
            this.$getHeader(true).disable(true);
            await Utils.sleep(100);
        }

        if (is_all === true) {
            /**
             * 기억된 모든 선택사항을 해제한다.
             */
            const chunk = new Chunks([...this.selections.values()]);
            await chunk.execute(async (record) => {
                const row = this.getTable().getBody().getRowById(record.getId());
                row?.$getRow().removeClass('select');

                if (this.isCascade() == true && row !== null) {
                    this.#cascadeSetSelectable(row, true);
                }
            });
            this.selections.clear();
        } else if (this.isCascade() == true) {
            /**
             * 트리 종속모드: 현재 스토어의 루트행 기준으로 선택해제 (cascade 복원 포함)
             */
            const chunk = new Chunks([...this.selections.values()]);
            await chunk.execute(async (record) => {
                if (this.getTable().getStore().hasRecordId(record.getId()) == true) {
                    await this.#deselect(record, false);
                }
            });
        } else {
            /**
             * 그리드 또는 트리 독립선택모드: 보이는 행 기준으로 선택해제
             */
            const recordIds = this.getTable().getBody().$getVisibleRows().getAttrs('data-record-id');
            const chunk = new Chunks(recordIds);
            await chunk.execute(async (recordId) => {
                if (this.selections.has(recordId) == true) {
                    const row = this.getTable().getBody().getRowById(recordId);
                    row?.$getRow().removeClass('select');
                    this.selections.delete(recordId);
                }
            });
        }

        if (is_fire_event == true) {
            await this.#selectionChange();
            this.$getHeader(true).enable();
        }
    }

    /**
     * 선택사항이 변경되었을 때 공통 처리사항을 처리한다.
     *
     * @param is_fire_event - 이벤트발생여부
     */
    async #selectionChange(is_fire_event: boolean = true): Promise<void> {
        if (this.multiple == true) {
            let is_all: boolean = false;

            if (this.getTable().isTree() == true && this.isCascade() == false) {
                /**
                 * 트리 독립선택모드: 현재 보이는 행이 모두 선택된 경우
                 */
                const recordIds = this.getTable().getBody().$getVisibleRows().getAttrs('data-record-id');
                const chunk = new Chunks(recordIds);
                await chunk.execute((recordId) => {
                    if (this.isSelected(recordId) == true) {
                        is_all = true;
                    } else {
                        is_all = false;
                        chunk.cancel();
                    }
                });
            } else {
                /**
                 * 그리드 또는 트리 종속모드: 스토어의 루트/전체 레코드 기준으로 판정
                 */
                const chunk = new Chunks(this.getTable().getStore().getRecords());
                await chunk.execute((record) => {
                    if (this.isSelected(record.getId()) == true) {
                        is_all = true;
                    } else {
                        is_all = false;
                        chunk.cancel();
                    }
                });
            }

            this.$getHeader(true).toggleClass('checked', is_all);
        }

        this.setSelectionView();

        if (is_fire_event == true) {
            this.getTable().fireEvent('selectionChange', [[...this.selections.values()], this.table]);
        }
    }

    /**
     * 데이터스토어가 새로 로드된 경우, 선택사항을 복원한다.
     */
    #restoreSelections(): void {
        this.#updateSelections(false);
        this.revalidateSelections();

        if (this.keepable == true) {
            let focused: boolean = false;
            this.selections.forEach((record) => {
                const row = this.getTable().getBody().getRowById(record.getId());
                if (row !== null) {
                    row.$getRow().addClass('select');
                    if (focused == false) {
                        this.getTable().getBody().focusRow(row, true, false);
                        focused = true;
                    }

                    if (this.isCascade() == true) {
                        this.#cascadeSetSelectable(row, false);
                    }
                }
            });
            this.#selectionChange(false);
        } else {
            this.selections.clear();
            this.#selectionChange();
        }
    }

    /**
     * 데이터스토어가 변경되었을 경우, 삭제된 레코드를 확인한다.
     */
    #updateSelections(is_fire_event: boolean = true): void {
        this.selections.forEach((record) => {
            if (this.getTable().getStore().getDeleted(record.getId()) !== null) {
                this.selections.delete(record.getId());
            }
        });

        if (is_fire_event == true) {
            this.#selectionChange(false);
        }
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.selections.clear();
        this.latestSelection = null;
        this.table = null;
    }
}
